import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Computer as ComputerIcon,
  Key as KeyIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

// Импорты сервисов и типов
import { api } from '../../services/api';
import type { Device } from '../../types';

interface DeviceDeleteDialogProps {
  open: boolean;
  device: Device;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

export const DeviceDeleteDialog: React.FC<DeviceDeleteDialogProps> = ({
  open,
  device,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [confirmationText, setConfirmationText] = useState('');
  const [forceDelete, setForceDelete] = useState(false);

  // Мутация для удаления устройства
  const deleteDeviceMutation = useMutation({
    mutationFn: () => api.deleteDevice(device.id, forceDelete),
    onSuccess: () => {
      onSuccess(t('devices.deviceDeleted', { name: device.name }));
      handleClose();
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.detail || t('devices.errorDeletingDevice'),
        { variant: 'error' }
      );
    },
  });

  // Обработчик закрытия диалога
  const handleClose = () => {
    setConfirmationText('');
    setForceDelete(false);
    onClose();
  };

  // Обработчик подтверждения удаления
  const handleConfirmDelete = () => {
    if (confirmationText !== device.name) {
      enqueueSnackbar(t('devices.confirmationTextMismatch'), { variant: 'error' });
      return;
    }

    deleteDeviceMutation.mutate();
  };

  // Проверка корректности ввода подтверждения
  const isConfirmationValid = confirmationText === device.name;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: '50vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div" color="error">
            <DeleteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('devices.deleteDevice')}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Предупреждение */}
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            <strong>{t('devices.deleteWarning')}</strong>
          </Typography>
          <Typography variant="body2">
            {t('devices.deleteWarningDescription')}
          </Typography>
        </Alert>

        {/* Информация об устройстве */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle1" gutterBottom color="primary">
            <ComputerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('devices.deviceToDelete')}
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <InfoIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('devices.name')}
                secondary={device.name}
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <InfoIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('devices.type')}
                secondary={device.device_type || device.type || 'N/A'}
              />
            </ListItem>
            
            {device.location && (
              <ListItem>
                <ListItemIcon>
                  <InfoIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t('devices.location')}
                  secondary={device.location}
                />
              </ListItem>
            )}
            
            <ListItem>
              <ListItemIcon>
                <InfoIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('devices.status')}
                secondary={
                  <Box component="span">
                    {device.status === 'online' && '🟢 '}
                    {device.status === 'offline' && '🔴 '}
                    {device.status === 'maintenance' && '🟡 '}
                    {device.status === 'error' && '❌ '}
                    {t(`devices.status.${device.status}` as any)}
                  </Box>
                }
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <InfoIcon />
              </ListItemIcon>
              <ListItemText
                primary={t('devices.deviceUuid')}
                secondary={device.device_uuid || device.uuid || 'N/A'}
              />
            </ListItem>
          </List>
        </Box>

        {/* Информация о связанных данных */}
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {t('devices.relatedDataWarning')}
          </Typography>
          
          <List dense>
            <ListItem>
              <ListItemIcon>
                <KeyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('devices.apiKeysWillBeDeleted')}
                secondary={t('devices.apiKeysWillBeDeletedDescription')}
              />
            </ListItem>
            
            <ListItem>
              <ListItemIcon>
                <HistoryIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={t('devices.historyWillBePreserved')}
                secondary={t('devices.historyWillBePreservedDescription')}
              />
            </ListItem>
          </List>
        </Alert>

        {/* Опция принудительного удаления */}
        <FormControlLabel
          control={
            <Checkbox
              checked={forceDelete}
              onChange={(e) => setForceDelete(e.target.checked)}
              color="error"
            />
          }
          label={
            <Box>
              <Typography variant="body2" color="error">
                {t('devices.forceDelete')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('devices.forceDeleteDescription')}
              </Typography>
            </Box>
          }
          sx={{ mb: 3 }}
        />

        {/* Поле подтверждения */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            {t('devices.confirmDeleteInstructions', { deviceName: device.name })}
          </Typography>
          
          <TextField
            fullWidth
            label={t('devices.confirmDeleteLabel')}
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            error={confirmationText.length > 0 && !isConfirmationValid}
            helperText={
              confirmationText.length > 0 && !isConfirmationValid
                ? t('devices.confirmationTextMismatch')
                : t('devices.typeDeviceNameToConfirm')
            }
            placeholder={device.name}
            autoComplete="off"
            InputProps={{
              sx: {
                '& input': {
                  fontFamily: 'monospace',
                }
              }
            }}
          />
        </Box>

        {deleteDeviceMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {deleteDeviceMutation.error?.message || t('devices.errorDeletingDevice')}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button 
          onClick={handleClose} 
          disabled={deleteDeviceMutation.isPending}
          variant="outlined"
        >
          {t('common.cancel')}
        </Button>
        
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirmDelete}
          disabled={deleteDeviceMutation.isPending || !isConfirmationValid}
          startIcon={deleteDeviceMutation.isPending ? <CircularProgress size={16} /> : <DeleteIcon />}
        >
          {deleteDeviceMutation.isPending 
            ? t('common.deleting') 
            : forceDelete 
              ? t('devices.forceDeleteDevice')
              : t('devices.deleteDevice')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 